import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Timer, CheckCircle, XCircle, Loader2, ArrowLeft, Trophy, AlertTriangle, Star, Sparkles, Volume2, VolumeX, Mic, MicOff } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { motion, AnimatePresence } from "framer-motion";
import { useSpeech } from "@/hooks/useSpeech";

const encouragePerQuestion = ["أحسنت! 👏", "ممتاز! ⭐", "برافو! 🌟", "رائع! 💪", "يا بطل! 🏆", "شاطر! 🎉"];
const optionColors = [
  "from-blue-400 to-blue-500",
  "from-green-400 to-green-500",
  "from-yellow-400 to-yellow-500",
  "from-pink-400 to-pink-500",
];

const TakeExam = () => {
  const { examId } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const { toast } = useToast();
  const nurseryId = profile?.nursery_id;

  const [selectedChildId, setSelectedChildId] = useState<string>("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [currentQ, setCurrentQ] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ score: number; max: number; percentage: number; details: any[] } | null>(null);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [started, setStarted] = useState(false);
  const [showEncourage, setShowEncourage] = useState<string | null>(null);
  const [answeredJustNow, setAnsweredJustNow] = useState<string | null>(null);

  // Speech integration
  const { speak, stopSpeaking, isSpeaking, isListening, voiceResult, startListening, stopListening, clearVoiceResult } = useSpeech({
    onVoiceResult: (transcript) => {
      if (!examQuestions || !started || result) return;
      const current = examQuestions[currentQ];
      const q = current?.question;
      if (!q || answers[q.id]) return;

      if (q.question_type === "multiple_choice" && q.options) {
        const normalizeAr = (s: string) => s.replace(/[^\u0600-\u06FF\s\w]/g, "").trim().toLowerCase();
        const spoken = normalizeAr(transcript);
        const match = (q.options as string[]).find((opt: string) => {
          const n = normalizeAr(opt);
          return spoken.includes(n) || n.includes(spoken);
        });
        if (match) setAnswer(q.id, match);
      } else if (q.question_type === "true_false") {
        const t = transcript.trim().toLowerCase();
        if (t.includes("صح") || t.includes("صحيح") || t.includes("نعم") || t.includes("true")) {
          setAnswer(q.id, "true");
        } else if (t.includes("خطأ") || t.includes("غلط") || t.includes("لا") || t.includes("false")) {
          setAnswer(q.id, "false");
        }
      } else if (q.question_type === "essay") {
        setAnswers(prev => ({ ...prev, [q.id]: (prev[q.id] || "") + " " + transcript }));
      }
    },
  });

  const { data: exam, isLoading: examLoading } = useQuery({
    queryKey: ["exam-detail", examId],
    queryFn: async () => {
      const { data, error } = await supabase.from("exams").select("*, classes(name)").eq("id", examId!).single();
      if (error) throw error;
      return data;
    },
    enabled: !!examId,
  });

  const { data: examQuestions, isLoading: questionsLoading } = useQuery({
    queryKey: ["exam-questions", examId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("exam_questions")
        .select("id, sort_order, points, question_id")
        .eq("exam_id", examId!)
        .order("sort_order", { ascending: true });
      if (error) throw error;
      const qIds = (data || []).map((eq: any) => eq.question_id);
      if (!qIds.length) return [];
      // Only fetch non-sensitive columns - correct_answer and explanation are hidden from parents
      const { data: questions } = await supabase.from("questions")
        .select("id, nursery_id, lesson_id, question_type, difficulty, question_text, options, subject, stage, is_ai_generated, created_at, updated_at")
        .in("id", qIds);
      const qMap = new Map((questions || []).map((q: any) => [q.id, q]));
      return (data || []).map((eq: any) => ({ ...eq, question: qMap.get(eq.question_id) })).filter((eq: any) => eq.question);
    },
    enabled: !!examId,
  });

  const { data: children } = useQuery({
    queryKey: ["children-for-exam", nurseryId, exam?.class_id],
    queryFn: async () => {
      let q = supabase.from("children").select("id, full_name").eq("nursery_id", nurseryId!);
      if (exam?.class_id) q = q.eq("class_id", exam.class_id);
      const { data } = await q.order("full_name");
      return data || [];
    },
    enabled: !!nurseryId && !!exam,
  });

  useEffect(() => {
    if (!started || !exam?.duration_minutes || timeLeft === null) return;
    if (timeLeft <= 0) { handleSubmit(); return; }
    const interval = setInterval(() => setTimeLeft((t) => (t !== null ? t - 1 : null)), 1000);
    return () => clearInterval(interval);
  }, [started, timeLeft]);

  const startExam = () => {
    if (!selectedChildId) { toast({ title: "خطأ", description: "اختر الطفل أولاً", variant: "destructive" }); return; }
    setStarted(true);
    if (exam?.duration_minutes) setTimeLeft(exam.duration_minutes * 60);
  };

  const setAnswer = useCallback((questionId: string, answer: string) => {
    setAnswers((prev) => ({ ...prev, [questionId]: answer }));
    setAnsweredJustNow(questionId);
    // Show encouragement
    const msg = encouragePerQuestion[Math.floor(Math.random() * encouragePerQuestion.length)];
    setShowEncourage(msg);
    setTimeout(() => setShowEncourage(null), 1500);
    // Auto-advance after short delay
    setTimeout(() => {
      if (examQuestions && currentQ < examQuestions.length - 1) {
        setCurrentQ((p) => p + 1);
      }
    }, 1200);
  }, [examQuestions, currentQ]);

  const handleSubmit = async () => {
    if (!examId || !selectedChildId || !examQuestions) return;
    setSubmitting(true);
    try {
      // Build answers array for server-side grading
      const answersArray = examQuestions.map((eq: any) => ({
        question_id: eq.question.id,
        answer: answers[eq.question.id] || "",
      }));

      // Use server-side grading function (correct_answer never exposed to client)
      const { data: gradeResult, error: gradeError } = await supabase.rpc("grade_exam", {
        _exam_id: examId,
        _child_id: selectedChildId,
        _answers: answersArray,
      });

      if (gradeError) throw gradeError;

      const result = gradeResult as any;
      setResult({
        score: result.score,
        max: result.max,
        percentage: result.percentage,
        details: result.details,
      });
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const answeredCount = examQuestions ? examQuestions.filter((eq: any) => answers[eq.question.id]).length : 0;

  if (examLoading || questionsLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="text-6xl animate-bounce">📝</div>
        <p className="text-muted-foreground text-lg">جاري تحميل الامتحان...</p>
      </div>
    );
  }

  if (!exam || !examQuestions?.length) {
    return (
      <div className="text-center py-20 space-y-4">
        <div className="text-6xl">😔</div>
        <p className="text-muted-foreground text-lg">لم يتم العثور على الامتحان</p>
        <Button variant="outline" onClick={() => navigate("/dashboard/lessons")} className="rounded-full">
          <ArrowLeft className="w-4 h-4 ml-2" />العودة
        </Button>
      </div>
    );
  }

  // ✨ Result Screen - Game-like
  if (result) {
    const stars = result.percentage >= 90 ? 3 : result.percentage >= 60 ? 2 : result.percentage >= 30 ? 1 : 0;
    return (
      <div className="max-w-lg mx-auto space-y-6">
        <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring" }}>
          <Card className="overflow-hidden">
            <div className={`p-8 text-center space-y-4 ${
              result.percentage >= 70 ? "bg-gradient-to-b from-yellow-50 to-green-50 dark:from-yellow-900/10 dark:to-green-900/10" 
              : "bg-gradient-to-b from-orange-50 to-amber-50 dark:from-orange-900/10 dark:to-amber-900/10"
            }`}>
              {/* Stars */}
              <div className="flex justify-center gap-2">
                {[1, 2, 3].map((s) => (
                  <motion.div
                    key={s}
                    initial={{ scale: 0, rotate: -180 }}
                    animate={{ scale: s <= stars ? 1 : 0.5, rotate: 0 }}
                    transition={{ delay: s * 0.3, type: "spring" }}
                  >
                    <Star className={`w-14 h-14 ${s <= stars ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}`} />
                  </motion.div>
                ))}
              </div>

              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 1 }}>
                <div className="text-6xl mb-2">{result.percentage >= 90 ? "🏆" : result.percentage >= 60 ? "🎉" : "💪"}</div>
                <h2 className="text-3xl font-black text-foreground">
                  {result.percentage >= 90 ? "ممتاز!" : result.percentage >= 60 ? "أحسنت!" : "حاول مرة أخرى!"}
                </h2>
                <p className="text-5xl font-black text-primary mt-2">{result.percentage}%</p>
                <p className="text-muted-foreground text-lg mt-1">{result.score} من {result.max}</p>
              </motion.div>
            </div>
          </Card>
        </motion.div>

        <h3 className="font-bold text-lg flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-yellow-500" />
          مراجعة الإجابات
        </h3>
        <div className="space-y-3">
          {result.details.map((d, i) => (
            <motion.div key={i} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}>
              <Card className={`border-2 ${d.is_correct === true ? "border-green-300 bg-green-50/50 dark:border-green-800 dark:bg-green-900/10" : d.is_correct === false ? "border-red-300 bg-red-50/50 dark:border-red-800 dark:bg-red-900/10" : "border-muted"}`}>
                <CardContent className="p-4 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium">{i + 1}. {d.question_text}</p>
                    <span className="text-2xl shrink-0">{d.is_correct === true ? "✅" : d.is_correct === false ? "❌" : "📝"}</span>
                  </div>
                  <div className="text-sm space-y-1">
                    <p><span className="text-muted-foreground">إجابتك:</span> <span className={d.is_correct === false ? "text-red-600 font-medium" : "font-medium"}>{d.answer || "—"}</span></p>
                    {d.is_correct === false && (
                      <p><span className="text-muted-foreground">الصحيحة:</span> <span className="text-green-600 font-bold">{d.correct_answer}</span></p>
                    )}
                    {d.explanation && <p className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-2 mt-1">💡 {d.explanation}</p>}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
        <Button onClick={() => navigate("/dashboard/lessons")} className="w-full rounded-full h-12 text-base">
          <ArrowLeft className="w-5 h-5 ml-2" />العودة للدروس
        </Button>
      </div>
    );
  }

  // 🎮 Pre-start Screen
  if (!started) {
    return (
      <div className="max-w-md mx-auto space-y-6">
        <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}>
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-b from-primary/10 to-primary/5 p-8 text-center space-y-4">
              <div className="text-7xl">📝</div>
              <h2 className="text-2xl font-black text-foreground">{exam.title}</h2>
              {(exam as any).classes?.name && (
                <Badge variant="outline" className="text-sm px-4 py-1 rounded-full">{(exam as any).classes.name}</Badge>
              )}
              <div className="flex justify-center gap-6 text-muted-foreground">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{examQuestions.length}</p>
                  <p className="text-xs">سؤال</p>
                </div>
                {exam.duration_minutes && (
                  <div className="text-center">
                    <p className="text-2xl font-bold text-foreground">{exam.duration_minutes}</p>
                    <p className="text-xs">دقيقة</p>
                  </div>
                )}
              </div>
            </div>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label className="font-medium">👦 اختر الطفل</Label>
                <Select value={selectedChildId} onValueChange={setSelectedChildId}>
                  <SelectTrigger className="rounded-xl h-12"><SelectValue placeholder="اختر الطفل" /></SelectTrigger>
                  <SelectContent>
                    {(children || []).map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>{c.full_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full h-14 text-lg rounded-full bg-gradient-to-l from-primary to-primary/80 shadow-lg" onClick={startExam} disabled={!selectedChildId}>
                🚀 ابدأ الامتحان
              </Button>
            </CardContent>
          </Card>
        </motion.div>
        <Button variant="ghost" onClick={() => navigate("/dashboard/lessons")} className="w-full rounded-full">
          <ArrowLeft className="w-4 h-4 ml-2" />العودة
        </Button>
      </div>
    );
  }

  // 🎮 Exam in progress - Game-like UI
  const current = examQuestions[currentQ];
  const q = current?.question;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Encouragement overlay */}
      <AnimatePresence>
        {showEncourage && (
          <motion.div
            initial={{ opacity: 0, scale: 0.5, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.5, y: -20 }}
            className="fixed top-1/3 left-1/2 -translate-x-1/2 z-50 pointer-events-none"
          >
            <div className="bg-primary text-primary-foreground px-8 py-4 rounded-2xl text-2xl font-black shadow-2xl">
              {showEncourage}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="flex items-center justify-between bg-card rounded-2xl p-4 shadow-sm border">
        <div className="flex items-center gap-3">
          <span className="text-2xl">📝</span>
          <div>
            <h2 className="font-bold">{exam.title}</h2>
            <p className="text-xs text-muted-foreground">سؤال {currentQ + 1} من {examQuestions.length}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* TTS: Read question */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-full"
            onClick={() => {
              if (isSpeaking) { stopSpeaking(); }
              else if (q) { speak(q.question_text + (q.options ? ". الخيارات: " + (q.options as string[]).join("، ") : "")); }
            }}
          >
            {isSpeaking ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </Button>
          {/* STT: Answer by voice */}
          <Button
            variant={isListening ? "destructive" : "outline"}
            size="sm"
            className={`gap-1 rounded-full text-xs ${isListening ? "animate-pulse" : ""}`}
            onClick={() => isListening ? stopListening() : startListening()}
          >
            {isListening ? <MicOff className="w-3.5 h-3.5" /> : <Mic className="w-3.5 h-3.5" />}
            {isListening ? "إيقاف" : "🎤"}
          </Button>
          {timeLeft !== null && (
            <Badge variant={timeLeft < 60 ? "destructive" : "secondary"} className="flex items-center gap-1 text-sm px-3 py-1 rounded-full">
              <Timer className="w-3.5 h-3.5" />
              {formatTime(timeLeft)}
            </Badge>
          )}
        </div>
      </div>

      {/* Voice result preview */}
      <AnimatePresence>
        {voiceResult && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="bg-muted/50 rounded-xl p-3 text-sm text-center"
          >
            <span className="text-xs text-muted-foreground block mb-1">🎤 ما سمعته:</span>
            <span className="font-medium text-foreground">"{voiceResult}"</span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress bar */}
      <div className="space-y-2">
        <Progress value={((answeredCount) / examQuestions.length) * 100} className="h-3 rounded-full" />
        <div className="flex justify-between text-xs text-muted-foreground px-1">
          <span>{answeredCount} من {examQuestions.length} تم الإجابة</span>
          <span>{Math.round(((answeredCount) / examQuestions.length) * 100)}%</span>
        </div>
      </div>

      {/* Question dots */}
      <div className="flex gap-1.5 flex-wrap justify-center">
        {examQuestions.map((_: any, i: number) => (
          <button
            key={i}
            className={`w-8 h-8 rounded-full text-xs font-bold transition-all ${
              i === currentQ
                ? "bg-primary text-primary-foreground scale-110 shadow-md"
                : answers[examQuestions[i].question.id]
                ? "bg-green-400 text-white"
                : "bg-muted text-muted-foreground"
            }`}
            onClick={() => setCurrentQ(i)}
          >
            {i + 1}
          </button>
        ))}
      </div>

      {/* Question Card - Colorful */}
      {q && (
        <motion.div key={currentQ} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", duration: 0.4 }}>
          <Card className="border-2 overflow-hidden">
            <div className="bg-gradient-to-l from-primary/10 to-primary/5 px-6 py-5">
              <p className="text-lg font-bold text-foreground leading-relaxed">{q.question_text}</p>
            </div>
            <CardContent className="p-5 space-y-3">
              {q.question_type === "multiple_choice" && q.options && (
                <div className="space-y-2">
                  {q.options.map((opt: string, oi: number) => {
                    const isSelected = answers[q.id] === opt;
                    return (
                      <motion.button
                        key={oi}
                        whileTap={{ scale: 0.97 }}
                        className={`w-full text-right rounded-xl p-4 border-2 transition-all flex items-center gap-3 ${
                          isSelected
                            ? "border-primary bg-primary/10 shadow-md"
                            : "border-muted hover:border-primary/50 hover:bg-muted/50"
                        }`}
                        onClick={() => setAnswer(q.id, opt)}
                      >
                        <span className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 bg-gradient-to-br ${optionColors[oi % optionColors.length]} text-white`}>
                          {String.fromCharCode(1571 + oi)}
                        </span>
                        <span className={`flex-1 font-medium ${isSelected ? "text-primary" : "text-foreground"}`}>{opt}</span>
                        {isSelected && <CheckCircle className="w-5 h-5 text-primary shrink-0" />}
                      </motion.button>
                    );
                  })}
                </div>
              )}

              {q.question_type === "true_false" && (
                <div className="grid grid-cols-2 gap-3">
                  {[{ value: "true", label: "صح ✓", emoji: "✅" }, { value: "false", label: "خطأ ✗", emoji: "❌" }].map((opt) => {
                    const isSelected = answers[q.id] === opt.value;
                    return (
                      <motion.button
                        key={opt.value}
                        whileTap={{ scale: 0.95 }}
                        className={`rounded-xl p-5 border-2 text-center transition-all ${
                          isSelected ? "border-primary bg-primary/10 shadow-md" : "border-muted hover:border-primary/50"
                        }`}
                        onClick={() => setAnswer(q.id, opt.value)}
                      >
                        <span className="text-3xl block mb-1">{opt.emoji}</span>
                        <span className="font-bold text-lg">{opt.label}</span>
                      </motion.button>
                    );
                  })}
                </div>
              )}

              {q.question_type === "essay" && (
                <Textarea
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswers(prev => ({ ...prev, [q.id]: e.target.value }))}
                  placeholder="اكتب إجابتك هنا..."
                  rows={4}
                  className="rounded-xl text-base"
                />
              )}
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Navigation */}
      <div className="flex justify-between gap-3">
        <Button variant="outline" disabled={currentQ === 0} onClick={() => setCurrentQ((p) => p - 1)} className="rounded-full px-6">
          السابق
        </Button>
        {currentQ < examQuestions.length - 1 ? (
          <Button onClick={() => setCurrentQ((p) => p + 1)} className="rounded-full px-6">
            التالي ←
          </Button>
        ) : (
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded-full px-8 bg-gradient-to-l from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white shadow-lg"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin ml-2" /> : "🎯"} تسليم الامتحان
          </Button>
        )}
      </div>
    </div>
  );
};

export default TakeExam;
